/**
 * Feedback Collector Service
 * Collects session data to help improve matching algorithms
 *
 * Enable via: localStorage.setItem('DEMOPATH_COLLECT_FEEDBACK', 'true')
 * View data: localStorage.getItem('DEMOPATH_FEEDBACK_SESSIONS')
 * Clear data: localStorage.removeItem('DEMOPATH_FEEDBACK_SESSIONS')
 */

import type { AgendaItem } from '@/types';

export interface MatchFeedback {
  itemId: string;
  itemText: string;
  keywords: string[];
  expandedPhrases?: string[];
  matched: boolean;
  confidence?: number;
  matchedText?: string;
  coveredAt?: number; // timestamp
}

export interface SessionFeedback {
  id: string;
  timestamp: number;
  duration: number; // ms

  // Agenda
  totalItems: number;
  trackableItems: number; // excludes headers

  // Results
  hitRate: number;
  matchedCount: number;
  missedCount: number;

  // Details
  items: MatchFeedback[];
  transcript: string[];

  // Metadata
  llmExpansionUsed: boolean;
  sweepMatchCount: number; // items matched by sweep vs real-time
}

const STORAGE_KEY = 'DEMOPATH_FEEDBACK_SESSIONS';
const FEATURE_FLAG_KEY = 'DEMOPATH_COLLECT_FEEDBACK';
const MAX_SESSIONS = 50; // Keep last 50 sessions

/**
 * Check if feedback collection is enabled
 */
export function isFeedbackEnabled(): boolean {
  try {
    return localStorage.getItem(FEATURE_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Enable feedback collection
 */
export function enableFeedback(): void {
  try {
    localStorage.setItem(FEATURE_FLAG_KEY, 'true');
    console.log('✅ Feedback collection enabled');
  } catch (e) {
    console.warn('Failed to enable feedback:', e);
  }
}

/**
 * Disable feedback collection
 */
export function disableFeedback(): void {
  try {
    localStorage.removeItem(FEATURE_FLAG_KEY);
    console.log('❌ Feedback collection disabled');
  } catch (e) {
    console.warn('Failed to disable feedback:', e);
  }
}

/**
 * Get all stored feedback sessions
 */
export function getFeedbackSessions(): SessionFeedback[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

/**
 * Save a feedback session
 */
export function saveFeedbackSession(session: SessionFeedback): void {
  if (!isFeedbackEnabled()) return;

  try {
    const sessions = getFeedbackSessions();
    sessions.push(session);

    // Keep only the last N sessions
    const trimmed = sessions.slice(-MAX_SESSIONS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    console.log(`📊 Feedback saved (${trimmed.length} sessions stored)`);
  } catch (e) {
    console.warn('Failed to save feedback:', e);
  }
}

/**
 * Clear all feedback data
 */
export function clearFeedbackSessions(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('🧹 Feedback data cleared');
  } catch (e) {
    console.warn('Failed to clear feedback:', e);
  }
}

/**
 * Export feedback data as downloadable JSON file
 */
export function exportFeedbackAsJson(): void {
  const sessions = getFeedbackSessions();

  if (sessions.length === 0) {
    alert('No feedback sessions to export');
    return;
  }

  const data = JSON.stringify(sessions, null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `demopath-feedback-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.log(`📥 Exported ${sessions.length} feedback sessions`);
}

/**
 * Export a single session as JSON
 */
export function exportSessionAsJson(session: SessionFeedback): void {
  const data = JSON.stringify(session, null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `demopath-session-${new Date(session.timestamp).toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build feedback from session data
 */
export function buildSessionFeedback(
  agenda: { items: AgendaItem[] },
  transcript: string[],
  startTime: number,
  llmExpansionUsed: boolean,
  sweepMatchCount: number
): SessionFeedback {
  const endTime = Date.now();
  const trackableItems = agenda.items.filter(i => !i.isGroupHeader);
  const matchedItems = trackableItems.filter(i => i.covered);
  const missedItems = trackableItems.filter(i => !i.covered);

  const items: MatchFeedback[] = trackableItems.map(item => ({
    itemId: item.id,
    itemText: item.text,
    keywords: item.keywords,
    expandedPhrases: (item as any)._expandedPhrases,
    matched: item.covered,
    confidence: item.confidence,
    matchedText: item.matchedText,
    coveredAt: item.coveredAt ?? undefined,
  }));

  return {
    id: `session-${startTime}`,
    timestamp: startTime,
    duration: endTime - startTime,
    totalItems: agenda.items.length,
    trackableItems: trackableItems.length,
    hitRate: trackableItems.length > 0
      ? Math.round((matchedItems.length / trackableItems.length) * 100)
      : 0,
    matchedCount: matchedItems.length,
    missedCount: missedItems.length,
    items,
    transcript,
    llmExpansionUsed,
    sweepMatchCount,
  };
}

/**
 * Generate a summary report of collected feedback
 */
export function generateFeedbackSummary(): string {
  const sessions = getFeedbackSessions();

  if (sessions.length === 0) {
    return 'No feedback sessions collected yet.';
  }

  const avgHitRate = sessions.reduce((sum, s) => sum + s.hitRate, 0) / sessions.length;
  const avgDuration = sessions.reduce((sum, s) => sum + s.duration, 0) / sessions.length;
  const totalMatched = sessions.reduce((sum, s) => sum + s.matchedCount, 0);
  const totalMissed = sessions.reduce((sum, s) => sum + s.missedCount, 0);

  // Find commonly missed items (items that appear multiple times and weren't matched)
  const missedTexts: Record<string, number> = {};
  for (const session of sessions) {
    for (const item of session.items) {
      if (!item.matched) {
        const key = item.itemText.toLowerCase().substring(0, 50);
        missedTexts[key] = (missedTexts[key] || 0) + 1;
      }
    }
  }

  const frequentMisses = Object.entries(missedTexts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  let report = `
DemoPath Feedback Summary
=========================
Sessions: ${sessions.length}
Total Items Tracked: ${totalMatched + totalMissed}
Average Hit Rate: ${avgHitRate.toFixed(1)}%
Average Duration: ${(avgDuration / 60000).toFixed(1)} minutes

Overall Stats:
- Matched: ${totalMatched} items
- Missed: ${totalMissed} items

LLM Expansion Usage: ${sessions.filter(s => s.llmExpansionUsed).length}/${sessions.length} sessions
Sweep Match Rate: ${sessions.reduce((sum, s) => sum + s.sweepMatchCount, 0)} items matched via sweep
`;

  if (frequentMisses.length > 0) {
    report += `
Frequently Missed Items (top 10):
${frequentMisses.map(([text, count]) => `  - "${text}..." (${count}x)`).join('\n')}
`;
  }

  return report;
}

// Expose to window for console debugging
if (typeof window !== 'undefined') {
  (window as any).demopathFeedback = {
    enable: enableFeedback,
    disable: disableFeedback,
    isEnabled: isFeedbackEnabled,
    getSessions: getFeedbackSessions,
    clear: clearFeedbackSessions,
    export: exportFeedbackAsJson,
    summary: () => console.log(generateFeedbackSummary()),
  };

  if (isFeedbackEnabled()) {
    console.log('📊 DemoPath feedback collection is ENABLED. Use demopathFeedback.summary() to see stats.');
  }
}
